import { MenuDivider, MenuItem, MenuList } from "@fluentui/react-components";
import React, { useMemo, ReactElement } from "react";
import { useNavigate } from "react-router";

import { useAppRouteParams } from "../AppRoutes";
import { getFileVersionViews } from "../api/files";
import { useAppNotificationManager } from "../components/AppProvider/AppNotificationProvider";

import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { nsView } from "@/i18n/locales/view";
import { generateId } from "@/utils/generateId";

const useTranslation = translation.use(nsVersion, nsView);

/**
 * @description 各種データ一覧画面で利用するcontextItemsを生成
 */
export const useContextMenuItemsVersionsMenu = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermissionWithId, hasPermission } = usePermissions();
  const hasVersionsWritePermission = hasPermission("versions", "write");
  const { organizations } = useAppOrgsAndInvitedOrgs();
  const { organizationId } = useAppRouteParams();
  const { versionCategories, isLoadedVersionCategories } =
    useVersionCategories();
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const hasAnyVersionWritePermission = useMemo(
    () =>
      hasVersionsWritePermission ||
      (versionCategories?.length
        ? versionCategories.some(({ id }) =>
            hasPermissionWithId("versions_version_category", "write", id)
          )
        : false),
    [hasVersionsWritePermission, versionCategories, hasPermissionWithId]
  );
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );
  const hasVersionCategoriesReadPermission = hasPermission(
    "version_categories",
    "read"
  );
  const hasVersionCategoriesWritePermission = hasPermission(
    "version_categories",
    "write"
  );

  const isDisableEdit = useMemo(() => {
    if (!hasAnyVersionWritePermission) return true;
    if (!hasVersionsMetaWritePermission) return true;
    if (organizationId && organization?.is_sheet_enabled === true) return false;
    return true;
  }, [
    hasAnyVersionWritePermission,
    hasVersionsMetaWritePermission,
    organizationId,
    organization,
  ]);

  const canManageVersionCategories =
    hasVersionCategoriesReadPermission && hasVersionCategoriesWritePermission;

  return useMemo(
    () => (
      <MenuList>
        <MenuItem
          key="k1"
          style={{ maxWidth: "none" }}
          onClick={() => {
            navigate(`/organizations/${organizationId}/versions/create`);
          }}
          disabled={isDisableEdit}
        >
          {t.version.create.newVersion()}
        </MenuItem>
        <MenuItem
          key="k2"
          style={{ maxWidth: "none" }}
          onClick={() => {
            navigate(
              `/organizations/${organizationId}/versions/upload_sheet_schema`
            );
          }}
          disabled={isDisableEdit}
        >
          {t.version.create.newVersionFromConfigFile()}
        </MenuItem>
        {canManageVersionCategories && (
          <>
            <MenuDivider />
            <MenuItem
              key="k3"
              style={{ maxWidth: "none" }}
              onClick={() => {
                navigate(
                  `/organizations/${organizationId}/organization_setting/version_categories/create`
                );
              }}
            >
              {t.version.category.create()}
            </MenuItem>
            <MenuItem
              key="k4"
              style={{ maxWidth: "none" }}
              onClick={() => {
                navigate(
                  `/organizations/${organizationId}/organization_setting/version_categories/sort`
                );
              }}
              disabled={
                !isLoadedVersionCategories || !versionCategories?.length
              }
            >
              {t.version.category.sort()}
            </MenuItem>
          </>
        )}
      </MenuList>
    ),
    [
      t,
      organizationId,
      isDisableEdit,
      canManageVersionCategories,
      isLoadedVersionCategories,
      versionCategories,
      navigate,
    ]
  );
};

export const useContextMenuItemsViewsMenu = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, versionId } = useAppRouteParams();
  const { hasPermission } = usePermissions();
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const {
    showInfoNotification,
    showSuccessNotification,
    showErrorNotification,
  } = useAppNotificationManager();

  return useMemo(
    () => (
      <MenuList>
        {hasVersionsMetaWritePermission && (
          <>
            <MenuItem
              key="i1"
              style={{ maxWidth: "none" }}
              onClick={async () => {
                const newViewId = generateId();
                navigate(
                  `/organizations/${organizationId}/versions/${versionId}/views/${newViewId}/edit`
                );
              }}
              disabled={!hasVersionsMetaWritePermission}
            >
              {t.view.create()}
            </MenuItem>
            <MenuDivider />
          </>
        )}
        <MenuItem
          key="i2"
          style={{ maxWidth: "none" }}
          onClick={async () => {
            try {
              showInfoNotification(t.view.configFile.downloadInProgress());
              await getFileVersionViews({
                organizationId,
                version: version!,
              });
              showSuccessNotification(t.view.configFile.downloadSuccess());
            } catch (error) {
              showErrorNotification(t.view.configFile.downloadError(), error);
            }
          }}
        >
          {t.view.configFile.bulkDownload()}
        </MenuItem>
        {hasVersionsMetaWritePermission && (
          <MenuItem
            key="i3"
            style={{ maxWidth: "none" }}
            onClick={() => {
              navigate(
                `/organizations/${organizationId}/versions/${versionId}/upload_views`
              );
            }}
            disabled={!hasVersionsMetaWritePermission}
          >
            {t.view.configFile.bulkUpload()}
          </MenuItem>
        )}
        <MenuDivider />
        <MenuItem
          key="i4"
          style={{ maxWidth: "none" }}
          onClick={() => {
            navigate(`/organizations/${organizationId}/versions/${versionId}`);
          }}
        >
          {t.view.openList()}
        </MenuItem>
      </MenuList>
    ),
    [
      t,
      version,
      versionId,
      organizationId,
      hasVersionsMetaWritePermission,
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
      navigate,
    ]
  );
};
